/**
 * Visit sources (BR-21). A list page tells its cards where they live (`ListSourceContext`); links
 * carry that value in router state (`{ src }`), and the shop / product page reads it on arrival.
 * Opened from outside the app: `?ref=share|whatsapp` → share, `?ref=qr` → qr, `?ref=c` (a campaign
 * post link, BR-31) → campaign, else direct.
 */
import { createContext, useContext } from 'react'

import type { VisitSource } from '@/types/api'

/** Sources a link inside the app can carry. */
export type ListSource = Extract<
  VisitSource,
  'home' | 'search' | 'category' | 'shops' | 'shop' | 'product'
>

const IN_APP: ReadonlySet<string> = new Set([
  'home',
  'search',
  'category',
  'shops',
  'shop',
  'product',
])

/** Lists whose cards count as "seen on home & search" (BR-21). */
export const SEEN_SOURCES: ReadonlySet<ListSource> = new Set(['home', 'search', 'category'])

export const ListSourceContext = createContext<ListSource | null>(null)

export const useListSource = () => useContext(ListSourceContext)

/** Router state to put on a link to a shop or product page. */
export const linkState = (src: ListSource | null) => (src ? { src } : undefined)

/** `?ref=` of a shared or printed link → source (BR-21); null when absent or unknown. */
export function refSource(search: string): VisitSource | null {
  const ref = new URLSearchParams(search).get('ref')?.toLowerCase()
  if (ref === 'share' || ref === 'whatsapp') return 'share'
  if (ref === 'qr') return 'qr'
  if (ref === 'c') return 'campaign'
  return null
}

/** Source of a page view from router state and the URL. */
export function arrivalSource(state: unknown, search: string): VisitSource {
  const src = (state as { src?: unknown } | null)?.src
  if (typeof src === 'string' && IN_APP.has(src)) return src as VisitSource
  return refSource(search) ?? 'direct'
}

/** Add `?ref=share` to a URL the app hands out (share sheet, copy link). */
export function withShareRef(url: string): string {
  const u = new URL(url, 'https://angadi.app')
  u.searchParams.set('ref', 'share')
  return /^https?:/.test(url) ? u.toString() : u.pathname + u.search + u.hash
}
